import React, { useEffect, useState } from "react";
import { RefreshCw, TrendingUp, IndianRupee, PercentCircle, ReceiptText } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatINR } from "@/lib/api";

// Detailed Margins — Selling Price - Purchase Cost = Net Profit, for every piece
// sold, costed at that exact piece's Master Bill cost (Source-Linked Itemization;
// see backend/fifo_audit.py::get_profit_margins).
// Sales that predate any cost tracking, or whose invoice was later deleted, are
// excluded from the list but surfaced as a transparency note below the table —
// never silently hidden.
export function ProfitMarginsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: d } = await api.get("/admin/reports/profit-margins");
      setData(d);
    } catch (_e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="profit-margins-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-medium">Detailed Margins</div>
          <p className="text-xs text-muted-foreground">Selling Price − Purchase Cost = Net Profit, per sold piece — cost comes from the exact Master Bill that piece was itemized from.</p>
        </div>
        <Button data-testid="margins-refresh-button" variant="outline" size="sm" className="gap-1" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="margins-kpi-revenue">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Total Revenue</span><IndianRupee className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-24" /> : formatINR(summary.total_revenue || 0)}</div>
        </Card>
        <Card className="p-4" data-testid="margins-kpi-cost">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Total Cost</span><ReceiptText className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-24" /> : formatINR(summary.total_cost || 0)}</div>
        </Card>
        <Card className="p-4" data-testid="margins-kpi-profit">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Net Profit</span><TrendingUp className="h-4 w-4 text-muted-foreground" /></div>
          <div className={`font-mono text-2xl mt-2 ${(summary.total_profit || 0) < 0 ? "text-[#7A1F1F]" : ""}`}>{loading ? <Skeleton className="h-7 w-24" /> : formatINR(summary.total_profit || 0)}</div>
        </Card>
        <Card className="p-4" data-testid="margins-kpi-margin-pct">
          <div className="flex items-center justify-between"><span className="text-xs text-muted-foreground uppercase">Margin %</span><PercentCircle className="h-4 w-4 text-muted-foreground" /></div>
          <div className="font-mono text-2xl mt-2">{loading ? <Skeleton className="h-7 w-16" /> : `${summary.margin_pct ?? 0}%`}</div>
        </Card>
      </div>

      {error ? (
        <Card className="p-8 text-center" data-testid="margins-error-state">
          <p className="text-sm text-muted-foreground mb-3">Could not load the margins report right now.</p>
          <button data-testid="margins-retry-button" onClick={load} className="text-sm text-[hsl(var(--primary))] underline underline-offset-2">Retry</button>
        </Card>
      ) : (
        <Card className="p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="margins-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Product / SKU</TableHead>
                  <TableHead>Piece</TableHead>
                  <TableHead>Source Bill</TableHead>
                  <TableHead>Invoice</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Selling Price</TableHead>
                  <TableHead className="text-right">Purchase Cost</TableHead>
                  <TableHead className="text-right">Net Profit</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={10}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center text-sm text-muted-foreground py-10">
                    No sales yet — itemize pieces from a Master Bill, then approve an order; every sold piece appears here with its own bill cost.
                  </TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.txn_id} data-testid={`margins-row-${r.txn_id}`}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{r.date ? new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}</TableCell>
                    <TableCell className="min-w-0">
                      <div className="text-sm truncate max-w-[220px]">{r.product_name || "—"}</div>
                      <div className="text-xs text-muted-foreground font-mono">{r.sku || "—"}</div>
                    </TableCell>
                    <TableCell className="text-xs font-mono whitespace-nowrap">{r.barcode || "—"}{r.gross_weight_g ? <div className="text-[10px] text-muted-foreground">{r.gross_weight_g}g{r.diamond_carat_total ? ` · ${r.diamond_carat_total}ct` : ""}</div> : null}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap"><span className="font-mono">{r.master_bill_no || "—"}</span>{r.source_name ? <div className="text-[10px] text-muted-foreground truncate max-w-[140px]">{r.source_name}</div> : null}</TableCell>
                    <TableCell className="text-xs font-mono">{r.invoice_no || r.order_no || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{r.quantity}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatINR(r.selling_price_total)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatINR(r.cost_total)}</TableCell>
                    <TableCell className={`text-right font-mono text-sm ${r.net_profit < 0 ? "text-[#7A1F1F]" : "text-[#1F5B2E]"}`}>{formatINR(r.net_profit)}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary" className="font-mono">{r.margin_pct}%</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {!loading && !error && (summary.uncosted_count > 0 || summary.deleted_order_count > 0) && (
        <p className="text-xs text-muted-foreground" data-testid="margins-transparency-note">
          {summary.uncosted_count > 0 && `${summary.uncosted_count} sale(s) predate piece-level costing and have no bill cost. `}
          {summary.deleted_order_count > 0 && `${summary.deleted_order_count} sale(s) belong to invoices that were later deleted, so no selling price is available for them.`}
        </p>
      )}
    </div>
  );
}
